import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { transformDishes, transformRestaurants, transformAdsResponse, transformSponsorshipsResponse, transformRestaurant, transformCategories, transformDealsResponse } from './serializers';
import { getCurrentPositionWithFallback, getCachedLocation } from '@features/map/utils/locationUtils.js';

// ============ LOAD NEARBY dishes ============
bus.on(constants.EVENTS.HOME.NEARBY_LOAD, async (payload = {}) => {
  console.log('[Controller] NEARBY_LOAD event received', payload);
  const requestId = `nearbyDishes-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.NEARBY_LOADING);
  console.log('[Controller] NEARBY_LOADING emitted');

  // Récupération des coordonnées utilisateur (payload explicite > position actuelle/fallback > cache)
  let lat = payload?.lat;
  let lng = payload?.lng;

  if (lat == null || lng == null) {
    try {
      const position = await getCurrentPositionWithFallback({
        preferCache: true,
        timeout: 2500
      });
      if (position && typeof position.lat === 'number' && typeof position.lng === 'number') {
        lat = position.lat;
        lng = position.lng;
      }
    } catch (locErr) {
      console.warn('[Controller] Impossible de récupérer la position pour nearby:', locErr);
      const cached = getCachedLocation();
      if (cached && typeof cached.lat === 'number' && typeof cached.lng === 'number') {
        lat = cached.lat;
        lng = cached.lng;
      }
    }
  }

  // Construction de l'URL avec les coordonnées si disponibles
  let nearbyUrl = constants.ENDPOINTS.HOME.NEARBY;
  if (lat != null && lng != null) {
    const separator = nearbyUrl.includes('?') ? '&' : '?';
    nearbyUrl = `${nearbyUrl}${separator}lat=${lat}&lng=${lng}`;
  }

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: nearbyUrl,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', nearbyUrl);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for NEARBY_LOAD:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming dishes data:', res.data);
        // Transform API data to component format
        const transformedDishes = transformDishes(res.data);
        console.log('[Controller] Transformed dishes:', transformedDishes);
        console.log('[Controller] Emitting NEARBY_LOADED with', transformedDishes.length, 'dishes');
        bus.emit(constants.EVENTS.HOME.NEARBY_LOADED, transformedDishes);
      } else {
        console.error('[Controller] NEARBY_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.NEARBY_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});


// ============ LOAD RECOMMENDED dishes ============
bus.on(constants.EVENTS.HOME.RECOMMENDED_LOAD, (payload = {}) => {
  console.log('[Controller] RECOMMENDED_LOAD event received', payload);
  const requestId = `recommendedDishes-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.RECOMMENDED_LOADING);
  console.log('[Controller] RECOMMENDED_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.RECOMMENDED,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.RECOMMENDED);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for RECOMMENDED_LOAD:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming dishes data:', res.data);
        // Transform API data to component format
        const transformedDishes = transformDishes(res.data);
        console.log('[Controller] Transformed dishes:', transformedDishes);
        console.log('[Controller] Emitting RECOMMENDED_LOADED with', transformedDishes.length, 'dishes');
        bus.emit(constants.EVENTS.HOME.RECOMMENDED_LOADED, transformedDishes);
      } else {
        console.error('[Controller] RECOMMENDED_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.RECOMMENDED_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});



// ============ LOAD POPULAR RESTAURANTS ============
bus.on(constants.EVENTS.HOME.POPULAR_RESTAURANTS_LOAD, (payload = {}) => {
  console.log('[Controller] POPULAR_RESTAURANTS_LOAD event received', payload);
  const requestId = `popularRestaurants-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.POPULAR_RESTAURANTS_LOADING);
  console.log('[Controller] POPULAR_RESTAURANTS_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.POPULAR,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.POPULAR);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for POPULAR RESTAURANTS:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming restaurants data:', res.data);
        // Transform API data to component format
        const transformedRestaurants = transformRestaurants(res.data);
        console.log('[Controller] Transformed restaurants:', transformedRestaurants);
        console.log('[Controller] Emitting POPULAR_RESTAURANTS_LOADED with', transformedRestaurants.length, 'restaurants');
        bus.emit(constants.EVENTS.HOME.POPULAR_RESTAURANTS_LOADED, transformedRestaurants);
      } else {
        console.error('[Controller] POPULAR_RESTAURANTS_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.POPULAR_RESTAURANTS_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD NEW RESTAURANTS ============
bus.on(constants.EVENTS.HOME.NEW_RESTAURANTS_LOAD, (payload = {}) => {
  console.log('[Controller] NEW_RESTAURANTS_LOAD event received', payload);
  const requestId = `newRestaurants-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.NEW_RESTAURANTS_LOADING);
  console.log('[Controller] NEW_RESTAURANTS_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.NEW,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.NEW);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for NEW RESTAURANTS:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming restaurants data:', res.data);
        // Transform API data to component format
        const transformedRestaurants = transformRestaurants(res.data);
        console.log('[Controller] Transformed restaurants:', transformedRestaurants);
        console.log('[Controller] Emitting NEW_RESTAURANTS_LOADED with', transformedRestaurants.length, 'restaurants');
        bus.emit(constants.EVENTS.HOME.NEW_RESTAURANTS_LOADED, transformedRestaurants);
      } else {
        console.error('[Controller] NEW_RESTAURANTS_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.NEW_RESTAURANTS_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD CATEGORIES ============
bus.on(constants.EVENTS.HOME.CATEGORIES_LOAD, () => {
  console.log('[Controller] CATEGORIES_LOAD event received');
  const requestId = `categories-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.CATEGORIES_LOADING);
  console.log('[Controller] CATEGORIES_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.CATEGORIES,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.CATEGORIES);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for CATEGORIES:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming categories data:', res.data);
        // Transform API data to component format
        const transformedCategories = transformCategories(res.data);
        console.log('[Controller] Transformed categories:', transformedCategories);
        console.log('[Controller] Emitting CATEGORIES_LOADED with', transformedCategories.length, 'categories');
        bus.emit(constants.EVENTS.HOME.CATEGORIES_LOADED, transformedCategories);
      } else {
        console.error('[Controller] CATEGORIES_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.CATEGORIES_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD ADS ============
bus.on(constants.EVENTS.HOME.ADS_LOAD, () => {
  console.log('[Controller] ADS_LOAD event received');
  const requestId = `ads-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.ADS_LOADING);
  console.log('[Controller] ADS_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.ADS,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.ADS);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for ADS:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming ads data:', res.data);
        // Transform API data to component format
        const transformedAds = transformAdsResponse(res.data);
        console.log('[Controller] Transformed ads:', transformedAds);
        console.log('[Controller] Emitting ADS_LOADED with', transformedAds.length, 'ads');
        bus.emit(constants.EVENTS.HOME.ADS_LOADED, transformedAds);
      } else {
        console.error('[Controller] ADS_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.ADS_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD COUPONS ============
bus.on(constants.EVENTS.HOME.COUPONS_LOAD, () => {
  console.log('[Controller] COUPONS_LOAD event received');
  const requestId = `coupons-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.COUPONS_LOADING);
  console.log('[Controller] COUPONS_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.COUPONS,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.COUPONS);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for COUPONS:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        // API returns {success: true, data: [...]} so we need res.data.data
        const couponsData = res.data?.data?.data || res.data?.data || res.data || [];
        console.log('[Controller] Emitting COUPONS_LOADED with', couponsData?.length || 0, 'coupons');
        // Transform coupon data to match component expectations
        const transformedCoupons = Array.isArray(couponsData) ? couponsData.map(coupon => ({
          ...coupon,
          title: coupon.title || coupon.description || 'Special Offer',
          // Add computed fields for display
          displayDiscount: coupon.type === 'percentage'
            ? `${coupon.discount}%`
            : `${coupon.discount} XOF`
        })) : [];
        bus.emit(constants.EVENTS.HOME.COUPONS_LOADED, transformedCoupons);
      } else {
        console.error('[Controller] COUPONS_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.COUPONS_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD SPONSORSHIPS ============
bus.on(constants.EVENTS.HOME.SPONSORSHIPS_LOAD, async (payload = {}) => {
  console.log('[Controller] SPONSORSHIPS_LOAD event received', payload);
  const requestId = `sponsorships-${Date.now()}`;

  bus.emit(constants.EVENTS.HOME.SPONSORSHIPS_LOADING);
  console.log('[Controller] SPONSORSHIPS_LOADING emitted');

  // Fetch sponsorships
  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId + '-sponsorships',
    url: constants.ENDPOINTS.HOME.SPONSORSHIPS,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.SPONSORSHIPS);

  // Response handler for sponsorships
  const responseHandler = (res) => {
    if (res.id === requestId + '-sponsorships') {
      console.log('[Controller] Response received for SPONSORSHIPS:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        if (res.success) {
          console.log('[Controller] Transforming sponsorships data:', res.data);
          const transformedSponsorships = transformSponsorshipsResponse(res.data);
          console.log('[Controller] Transformed sponsorships:', transformedSponsorships);

          console.log('[Controller] Emitting SPONSORSHIPS_LOADED with', transformedSponsorships.length, 'items');
          bus.emit(constants.EVENTS.HOME.SPONSORSHIPS_LOADED, transformedSponsorships);
        } else {
          console.error('[Controller] SPONSORSHIPS_LOAD error:', res.data?.error || res.data);
          bus.emit(constants.EVENTS.HOME.SPONSORSHIPS_LOAD_ERROR, res.data?.error || res.data);
        }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD ALL RESTAURANTS ============
bus.on(constants.EVENTS.HOME.ALL_RESTAURANTS_LOAD, (payload = {}) => {
  console.log('[Controller] ALL_RESTAURANTS_LOAD event received', payload);
  const requestId = `allRestaurants-${Date.now()}`;

  bus.emit(constants.EVENTS.HOME.ALL_RESTAURANTS_LOADING);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.ALL_RESTAURANTS,
    method: 'GET',
    data: {}
  });

  const responseHandler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        // Transformations API list format to component format
        const transformed = transformRestaurants(res.data);
        bus.emit(constants.EVENTS.HOME.ALL_RESTAURANTS_LOADED, transformed);
      } else {
        bus.emit(constants.EVENTS.HOME.ALL_RESTAURANTS_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});

// ============ LOAD DEALS ============
bus.on(constants.EVENTS.HOME.DEALS_LOAD, (payload = {}) => {
  console.log('[Controller] DEALS_LOAD event received', payload);
  const requestId = `deals-${Date.now()}`;
  console.log('[Controller] Request ID:', requestId);

  bus.emit(constants.EVENTS.HOME.DEALS_LOADING);
  console.log('[Controller] DEALS_LOADING emitted');

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.HOME.DEALS,
    method: 'GET',
    data: {}
  });
  console.log('[Controller] NETWORK.REQUEST emitted for:', constants.ENDPOINTS.HOME.DEALS);

  // Écouter la réponse une seule fois
  const responseHandler = (res) => {
    if (res.id === requestId) {
      console.log('[Controller] Response received for DEALS:', res);
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        console.log('[Controller] Transforming deals data:', res.data);
        // Transform API data to component format
        const transformedDeals = transformDealsResponse(res.data);
        console.log('[Controller] Transformed deals:', transformedDeals);
        console.log('[Controller] Emitting DEALS_LOADED with', transformedDeals.length, 'deals');
        bus.emit(constants.EVENTS.HOME.DEALS_LOADED, transformedDeals);
      } else {
        console.error('[Controller] DEALS_LOAD error:', res.data?.error || res.data);
        bus.emit(constants.EVENTS.HOME.DEALS_LOAD_ERROR, res.data?.error || res.data);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
});
